import { STORAGE_KEYS } from '@/utils/constants';
import { shouldForceGuideRecruitmentCampaign } from '../config/guide-recruitment.campaign';

interface DismissalRecord {
  dismissedAt: string;
}

function dismissalKey(campaignId: string, userId?: string): string {
  const base = `${STORAGE_KEYS.GUIDE_CAMPAIGN_DISMISSED}:${campaignId}`;
  return userId ? `${base}:${userId}` : base;
}

function readDismissal(key: string): DismissalRecord | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DismissalRecord;
    if (!parsed?.dismissedAt) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function isCampaignDismissed(
  campaignId: string,
  cooldownDays: number,
  userId?: string,
): boolean {
  if (shouldForceGuideRecruitmentCampaign()) return false;

  const keys = userId
    ? [dismissalKey(campaignId, userId), dismissalKey(campaignId)]
    : [dismissalKey(campaignId)];

  const now = Date.now();
  const cooldownMs = cooldownDays * 24 * 60 * 60 * 1000;

  return keys.some((key) => {
    const record = readDismissal(key);
    if (!record) return false;
    const dismissedAt = new Date(record.dismissedAt).getTime();
    if (Number.isNaN(dismissedAt)) return false;
    return now - dismissedAt < cooldownMs;
  });
}

export function markCampaignDismissed(campaignId: string, userId?: string): void {
  const record: DismissalRecord = { dismissedAt: new Date().toISOString() };
  try {
    localStorage.setItem(dismissalKey(campaignId, userId), JSON.stringify(record));
    if (userId) {
      localStorage.setItem(dismissalKey(campaignId), JSON.stringify(record));
    }
  } catch {
    // Ignore quota / private mode failures.
  }
}

export function hasCampaignShownThisSession(): boolean {
  if (shouldForceGuideRecruitmentCampaign()) return false;
  try {
    return sessionStorage.getItem(STORAGE_KEYS.GUIDE_CAMPAIGN_SESSION_SHOWN) === '1';
  } catch {
    return false;
  }
}

export function markCampaignShownThisSession(): void {
  try {
    sessionStorage.setItem(STORAGE_KEYS.GUIDE_CAMPAIGN_SESSION_SHOWN, '1');
  } catch {
    // Ignore storage failures.
  }
}

export function hasEnteredGuideFlowThisSession(): boolean {
  if (shouldForceGuideRecruitmentCampaign()) return false;
  try {
    return sessionStorage.getItem(STORAGE_KEYS.GUIDE_FLOW_ENTERED_SESSION) === '1';
  } catch {
    return false;
  }
}

export function markGuideFlowEnteredSession(): void {
  try {
    sessionStorage.setItem(STORAGE_KEYS.GUIDE_FLOW_ENTERED_SESSION, '1');
  } catch {
    // Ignore storage failures.
  }
}
